import { useState } from "react";
import {
  useGetNexusStatusForStateQuery,
  useUpdatePhysicalNexusForStateMutation,
} from "store/apis/salesTax";
import { useInitiateSalesTaxRegistrationMutation } from "store/apis/salesTaxRegistrationDetails";
import { APPROACHING, EXPOSED, MONITORING } from "constants/nexusTrackerStatus";
import {
  REGISTERED as NEXUS_REGISTRATION_STATUS_REGISTERED,
  VALIDATING as NEXUS_REGISTRATION_STATUS_VALIDATING,
  FILING_IN_PROGRESS as NEXUS_REGISTRATION_STATUS_FILING_IN_PROGRESS,
  IN_REVIEW,
  PROCESSING,
} from "constants/salesTaxRegistrationStatusType";
import { CART_ITEM_STATUS } from "constants/cart";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import { getBillingCartUrl } from "util/getBillingCartUrl";
import { NexusComplianceInfo } from "./useLineItemTaxCompliance";

export const useInvoiceSalesTaxCompliance = ({
  entityId,
  groupId,
  buyerStateName,
}: {
  entityId: string;
  groupId: string;
  buyerStateName?: string | null;
}) => {
  const { alertToast } = useToast();
  const { isAdmin } = useRoleBasedView();
  const [isApproachingBannerDismissed, setIsApproachingBannerDismissed] =
    useState(false);

  const {
    data: nexusStatusResponse,
    isError: hasNexusStatusError,
    refetch,
  } = useGetNexusStatusForStateQuery(
    { entityId, stateName: buyerStateName || "" },
    { skip: !entityId || !buyerStateName }
  );

  const [updatePhysicalNexusForState, { isLoading: isConfirmingNoPresence }] =
    useUpdatePhysicalNexusForStateMutation();
  const [initiateRegistration, { isLoading: isRegisteringNow }] =
    useInitiateSalesTaxRegistrationMutation();

  const hasBuyerAddress = Boolean(buyerStateName);

  const nexusComplianceInfo: NexusComplianceInfo | null =
    nexusStatusResponse && !hasNexusStatusError && hasBuyerAddress
      ? {
          status: nexusStatusResponse.nexus_status,
          registrationStatus: nexusStatusResponse.nexus_registration_status,
          stateName: nexusStatusResponse.nexus_state_name,
          stateId: nexusStatusResponse.nexus_state_id,
          registrationId: nexusStatusResponse.nexus_registration_id,
          cartStatus: nexusStatusResponse.nexus_cart_status,
          physicalPresenceConfirmedAbsent:
            nexusStatusResponse.nexus_physical_presence_confirmed_absent,
          launchedTaskId: nexusStatusResponse.launched_task_id,
        }
      : null;

  const confirmNoPhysicalPresence = async () => {
    const stateId = nexusComplianceInfo?.stateId;
    if (!stateId) return;

    try {
      await updatePhysicalNexusForState({
        entityId,
        stateId,
        payload: {
          office_present: false,
          warehouse_present: false,
          employees_present: false,
        },
      }).unwrap();
      void refetch();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const registerNow = async () => {
    const registrationId = nexusComplianceInfo?.registrationId;
    if (!registrationId) return;

    try {
      await initiateRegistration({ entityId, registrationId }).unwrap();
      void refetch();
      const cartUrl = getBillingCartUrl({ isAdmin, groupId, entityId });
      window.open(cartUrl, "_blank");
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  const viewFiling = () => {
    const launchedTaskId = nexusComplianceInfo?.launchedTaskId;
    if (!launchedTaskId) return;

    window.open(`/tax/filings/${launchedTaskId}`, "_blank");
  };

  const needsComplianceAttention =
    nexusComplianceInfo?.status === EXPOSED ||
    nexusComplianceInfo?.status === APPROACHING;
  const isRegistrationInProgress =
    needsComplianceAttention &&
    (nexusComplianceInfo?.registrationStatus === IN_REVIEW ||
      nexusComplianceInfo?.registrationStatus === PROCESSING);
  const isFilingInProgress =
    needsComplianceAttention &&
    nexusComplianceInfo?.registrationStatus ===
      NEXUS_REGISTRATION_STATUS_FILING_IN_PROGRESS;
  const isPendingInCart =
    needsComplianceAttention &&
    nexusComplianceInfo?.registrationStatus ===
      NEXUS_REGISTRATION_STATUS_VALIDATING &&
    nexusComplianceInfo?.cartStatus === CART_ITEM_STATUS.IN_CART;
  const needsComplianceBanner =
    needsComplianceAttention &&
    !isRegistrationInProgress &&
    !isFilingInProgress &&
    !isPendingInCart &&
    nexusComplianceInfo?.registrationStatus !==
      NEXUS_REGISTRATION_STATUS_REGISTERED;

  const showNoTaxRequiredBanner =
    !nexusComplianceInfo?.physicalPresenceConfirmedAbsent &&
    nexusComplianceInfo?.status === MONITORING;

  return {
    nexusComplianceInfo,
    confirmNoPhysicalPresence,
    isConfirmingNoPresence,
    registerNow,
    isRegisteringNow,
    viewFiling,
    recalculate: refetch,
    showExposedComplianceBanner:
      needsComplianceBanner && nexusComplianceInfo?.status === EXPOSED,
    showApproachingComplianceBanner:
      !isApproachingBannerDismissed &&
      needsComplianceBanner &&
      nexusComplianceInfo?.status === APPROACHING,
    showCartPendingBanner: isPendingInCart,
    showPendingRegistrationBanner: isRegistrationInProgress,
    showFilingInProgressBanner: isFilingInProgress,
    showNoTaxRequiredBanner,
    dismissApproachingBanner: () => setIsApproachingBannerDismissed(true),
  };
};
